// charge-truth.ts — Lane ROLLS S3 (2026-09-27): the client-truth GM / SHEET CHARGE adapter + WS subscriber.
//
// The pure resolvers (janus-engine/charges/charge-adjust.ts) decide the change against the caster row the page holds; every row
// this page holds for the actor (the encounter member row, the caster-cache rows) is patched in place and rendered at once; the
// commit (options/tools/charges/charge_truth_commit_inc.php) CASes `before` against the `charges` row.
// N6 (2026-10-01): the legacy adjust_charge_inc.php / change_charges_inc.php / change_inventory_item_charges_inc.php are GONE (R-071…R-073).
// commitCharge() is the one seam: the shared actor-row loader gets the caster's row (held, or its encounter loaded; an encounter-less row
// is refused with words — N6c, owner 2026-10-01), a definition the page lacks is fetched once, the projectile-pool rule is read on the
// page (window.ftGetProjectilePool), and anything still undecidable is refused WITH WORDS.
// Every other page applies the committed `janus_charge_truth` frame; a stale answer / correction frame installs the SAVED count.
import {
  CHARGE_SCHEMA, resolveAdjustCharge, resolveInventoryCharge, resolveSetCharge, setAbilityCharges, setInventoryCharges,
  type ChargeKind, type ChargeReaders, type ChargeResolution,
} from '../janus-engine/charges/charge-adjust.js';
import { encounterCreatureField, encounterField, type CasterArr, type CampaignArr } from '../shared/array-types.js';
import { normalizePhpListish } from '../shared/php-listish.js';
import { handleCasterUpdate } from './caster-update.js';
import { actorRowFor, actorEncounterFor } from './movement-truth.js';
import { ensureActorRowHeld, runRowOnly } from '../shared/actor-row-loader.js';
import { fetchAndCacheObject } from './object-cache.js';
import { ETHER_MECHANIC_ID, PSI_MECHANIC_ID } from '../janus-engine/economy/out-of-combat.js';
import { chargeMarkerDue, planChargeMarker, emptyChargeMarkerDecision, type ChargeMarkerDecision } from '../janus-engine/charges/charge-marker-plan.js';
import { ecId, type JanusReaders } from '../janus-engine/ability/context.js';
import { HOLDING_MASTERY } from '../janus-engine/ability/charges/mastery-marker.js';
import { abilityTruthCampaign, abilityTruthReaders, fetchObjectsBatch, hydrateTurnTriggerDefinitions } from './ability-truth.js';
import { browserRollSource } from '../janus-engine/actions/weapon-attack.js';
import { askTriggerConsent, createTriggerConsent, offerFortunate } from './trigger-consent.js';
import { fullDefinition } from '../janus-engine/reactions/php-row-rules.js';
import { queueActorSave } from './applied-save-outbox';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { rowAbilityChargeUpdates } from '../janus-engine/ability/charges/charge-entry';
import { saveDiceTruth, publishSavedDice } from '../shared/dice-publication';
import { diceAppliedFrame } from '../janus-engine/transport/dice-applied-frame';
import { applyTurnTick } from '../janus-engine/turns/tick-frame';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const ENDPOINT = '/options/tools/charges/charge_truth_commit_inc.php';
/** A catalog definition the page holds: the object cache, then any cached encounter's / caster's interned catalog. */
export function definitionOf(id: string): Row | null {
  const key = upper(id);
  const ft = (window as unknown as { FateTide?: { objectCache?: Map<string, unknown>; encounterCache?: Map<string, unknown>; casterCache?: Map<string, unknown> } }).FateTide;
  const hosts: unknown[] = [ft?.objectCache?.get(key), ft?.objectCache?.get(id)];
  for (const enc of ft?.encounterCache?.values?.() ?? []) {
    const e = asRow(enc);
    hosts.push(asRow(e?.catalog)?.[key]);
    for (const m of Array.isArray(e?.encounter_creatures_arr) ? e!.encounter_creatures_arr as unknown[] : []) hosts.push(asRow(asRow(m)?.catalog)?.[key]);
  }
  for (const c of ft?.casterCache?.values?.() ?? []) hosts.push(asRow(asRow(c)?.catalog)?.[key]);
  for (const h of hosts) { const def = asRow(h); if (def && asRow(def.items)) return def; }
  return null;
}
export const readers: ChargeReaders = {
  entries: (row, key) => row ? (ftLayerEntries(row, key) ?? []) as unknown[] : [],
  value: (row, key) => row ? ftLayerValue(row, key) : null,
  definition: definitionOf,
  // N6: adjust_charges()'s upcast projectile-pool read (ft_projectile_pool) — the page twin lives on the sheet (action-bar.ts).
  projectilePool: (casterId, itemId) => {
    const read = (window as unknown as { ftGetProjectilePool?: (c: string, a: string) => unknown }).ftGetProjectilePool;
    return typeof read === 'function' ? read(casterId, itemId) !== null : null;
  },
};
const warn = (m: string): void => { if (typeof displayWarning === 'function') displayWarning(m); };
function chargeContext(encounterId: string, caster: Row): { encounter: Row; members: Map<string, Row> } | null {
  const encounter = asRow(actorEncounterFor(encounterId));
  if (!encounter) { lastChargeReason = 'charge-marker:encounter-not-held'; return null; }
  if (encounterField(encounter, 'encounter_master_parent_type') === 'campaign' && !abilityTruthCampaign(encounter)) {
    lastChargeReason = 'charge-marker:campaign-not-held'; return null;
  }
  const members = new Map(normalizePhpListish(encounter.encounter_creatures_arr).flatMap(value => {
    const member = asRow(value), id = ecId(member);
    return member && id ? [[id, member] as [string, Row]] : [];
  }));
  members.set(ecId(caster), caster);
  return { encounter, members };
}

/** Every row this page holds for the actor: the caster-cache rows first (the full compiled caster), then the encounter member. */
function rowsFor(encounterId: string, actorId: string): { row: Row; cache: boolean }[] {
  const out: { row: Row; cache: boolean }[] = [];
  const cache = (window as unknown as { FateTide?: { casterCache?: Map<string, unknown> } }).FateTide?.casterCache;
  for (const [key, value] of cache?.entries?.() ?? []) {
    const row = asRow(value);
    if (row && upper(key) === actorId) out.push({ row, cache: true });
  }
  const member = actorRowFor(encounterId, actorId) as unknown as Row | null;
  if (member && !out.some(o => o.row === member)) out.push({ row: member, cache: false });
  return out;
}
function install(kind: ChargeKind, encounterId: string, actorId: string, itemId: string, current: number): void {
  for (const { row, cache } of rowsFor(encounterId, actorId)) {
    const paths = kind === 'inventory' ? ['inventory.current_item_charges']
      : rowAbilityChargeUpdates(row, itemId, current).set.map(write => write.path.join('.'));
    const hit = kind === 'inventory' ? setInventoryCharges(row, itemId, current, readers) : setAbilityCharges(row, itemId, current, readers);
    if (hit && cache) handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true }, paths);
  }
  window.dispatchEvent(new CustomEvent('ft:charge-truth-applied', { detail: { kind, encounterId, actorId, itemId, current } }));
}

/**
 * Per-pool ordering (Codex review 2026-09-28 F7): the commit stamps every settled count with a per-pool revision (Redis INCR under the
 * pool lock), so a page installs a server count only when its revision is NEWER than the last one it saw; and while a local click on
 * the pool is still in flight, its optimistic count stays on screen (its own answer settles it).
 */
interface PoolState { rev: number; value: number | null; pending: string[] }
const pools = new Map<string, PoolState>();
const poolOf = (key: string): PoolState => {
  let p = pools.get(key);
  if (!p) { p = { rev: -1, value: null, pending: [] }; pools.set(key, p); if (pools.size > 512) pools.delete(pools.keys().next().value!); }
  return p;
};
/** Accept a server count at `rev`; install it unless a local click on the pool is still in flight (other than `ownOp`). */
function acceptServer(kind: ChargeKind, encounterId: string, actorId: string, itemId: string, current: number, rev: number, ownOp?: string): boolean {
  const p = poolOf(actorId + ':' + itemId);
  if (!(rev > p.rev)) return false;
  p.rev = rev; p.value = current;
  if (p.pending.some(op => op !== ownOp)) return false;
  install(kind, encounterId, actorId, itemId, current);
  return true;
}
const countOf = (v: unknown): number | null => typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null;
const revOf = (v: unknown): number => typeof v === 'number' && Number.isInteger(v) ? v : -1;

export type ChargeCommitOutcome = 'handled' | 'refused' | null;
export interface ChargeCommitInput { kind: ChargeKind; casterId: string; encounterId: string; itemId: string; delta?: unknown; value?: unknown; log?: boolean; onCombatStarted?: () => void }

async function post(body: Row): Promise<{ status: number; result: Row | null }> {
  try { return await saveDiceTruth(ENDPOINT, body as Row & { operation_id: string }); }
  catch (error) { return { status: 0, result: { error: String(error) } }; }
}

/** Null = unavailable before any mutation; commitCharge supplies the named refusal. */
export function tryCommitCharge(input: ChargeCommitInput, triggerConsent?: JanusReaders['triggerConsent']): ChargeCommitOutcome {
  const encounterId = upper(input.encounterId), actorId = upper(input.casterId), itemId = upper(input.itemId);
  if (!actorId || !itemId || !encounterId) return null; // N6c: an encounter-less row is never posted (commitCharge refuses it with words)
  const rows = rowsFor(encounterId, actorId);
  if (!rows.length) return null;
  const caster = rows[0]!.row;
  const res: ChargeResolution = input.kind === 'adjust' ? resolveAdjustCharge(caster, itemId, input.delta, readers, actorId)
    : input.kind === 'set' ? resolveSetCharge(caster, itemId, input.value, readers)
    : resolveInventoryCharge(caster, itemId, input.value, readers);
  if (!res.supported) { lastChargeReason = res.reason; window.dispatchEvent(new CustomEvent('ft:charge-slice-unavailable', { detail: { kind: input.kind, reason: res.reason } })); return null; }
  const plan = () => {
    if (!chargeMarkerDue(res)) return { supported: true as const, after: res.after, decision: emptyChargeMarkerDecision(), fortunateOffers: [] };
    const context = chargeContext(encounterId, caster);
    if (!context) return { supported: false as const, reason: lastChargeReason };
    const complete = new Set([...context.members].filter(([, member]) => asRow(asRow(member.computed)?.creature)
      && asRow(member.item_layer_types)).map(([id]) => id));
    const read = { ...abilityTruthReaders(caster, context.encounter, complete), triggerConsent: triggerConsent ?? createTriggerConsent(encounterId),
      ...(!abilityTruthCampaign(context.encounter) ? { campaignClock: () => null } : {}) };
    if (read.campaignClock() && !fullDefinition(read.definition(HOLDING_MASTERY), 'any'))
      return { supported: false as const, reason: `adjust_charges:mastery-marker:definition-not-held:${HOLDING_MASTERY}` };
    return planChargeMarker(res, actorId, context.encounter, context.members, read, browserRollSource());
  };
  const marker = plan();
  if (!marker.supported) { lastChargeReason = marker.reason; return null; }
  const oid = crypto.randomUUID().toUpperCase();
  const heldEncounter = asRow(actorEncounterFor(encounterId));
  if (!heldEncounter) { lastChargeReason = 'charge-marker:encounter-not-held'; return null; }
  const primary = { holder: res.kind === 'inventory' ? itemId : actorId, item_id: itemId, before: res.before, charges: marker.after };
  const applied = diceAppliedFrame(oid, encounterId, actorId,
    { ...marker.decision.verdict, charges: [...marker.decision.verdict.charges, primary] },
    heldEncounter, abilityTruthCampaign(heldEncounter), definitionOf);
  const pool = poolOf(actorId + ':' + itemId);
  pool.pending.push(oid);
  const confirmed: Array<{ actorId: string; rev: number; aps?: number }> = [];
  if (decisionHasWrites(marker.decision)) {
    const context = chargeContext(encounterId, caster)!;
    applyTurnTick([...context.members.values()], { ...marker.decision.verdict, statusesChanged: marker.decision.verdict.statusStacks }, definitionOf, context.encounter);
    // The typed boundary saver advances each written EC row once. Later actions
    // use that same decided revision while this pool's separate receipt is pending.
    for (const id of Object.keys(marker.decision.verdict.ec)) {
      const row = context.members.get(id), ec = asRow(row?.encounter_creatures);
      if (!ec) continue;
      const rev = Number(encounterCreatureField(ec, 'movement_rev') ?? 0) + 1;
      ec.movement_rev = rev;
      confirmed.push({ actorId: id, rev, ...(marker.decision.verdict.ec[id]?.aps === undefined ? {}
        : { aps: Number(encounterCreatureField(ec, 'aps') ?? 0) }) });
    }
  }
  install(res.kind, encounterId, actorId, itemId, marker.after);
  const body = { schema: CHARGE_SCHEMA, operation_id: oid, encounter_id: encounterId, caster_id: actorId, item_id: itemId, kind: res.kind,
    before: res.before, after: marker.after, delta: res.delta, log: input.log === true, decision: marker.decision };
  const applyAnswer = async ({ status, result }: { status: number; result: Row | null }): Promise<void> => {
    const settle = (): boolean => { pool.pending = pool.pending.filter(op => op !== oid); return pool.pending.length === 0; };
    /** Once nothing else is in flight, show the NEWEST server count this page accepted (a frame buffered while clicks were pending may
     *  be newer than this operation's own answer — Codex verification 7). */
    const drain = (): void => { if (pool.pending.length === 0 && pool.value !== null) install(res.kind, encounterId, actorId, itemId, pool.value); };
    const current = countOf(result?.current), rev = revOf(result?.rev);
    if (status === 200 && result?.ok === true) {
      settle();
      if (!acceptServer(res.kind, encounterId, actorId, itemId, current ?? marker.after, rev, oid)) drain();
      const saved = asRow(result.charge_row);
      if (saved) applied.rows = applied.rows.map(row => row.table === 'charges'
        && upper(readItemField(row.row, 'charge_holder')) === upper(readItemField(saved, 'charge_holder'))
        && upper(readItemField(row.row, 'item_id')) === itemId
        ? { table: 'charges', row_id: upper(readItemField(saved, 'charge_id')), row: saved } : row);
      // A lost-answer replay can return a newer settled count. Keep this pool's
      // raw row and changed compiled leaves aligned with that same answer.
      const changes = rowAbilityChargeUpdates(caster, itemId, current ?? marker.after);
      const delta = applied.computed_delta?.[actorId];
      if (delta && body.kind !== 'inventory') {
        const selected = new Map(changes.set.filter(write => write.path[0] === 'computed').map(write => [write.path.slice(1).join('.'), write.value]));
        delta.set = delta.set.map(write => selected.has(write.path.join('.')) ? { ...write, value: selected.get(write.path.join('.')) } : write);
        delta.before = delta.before?.filter(write => !selected.has(write.path.join('.')));
        if (delta.mirrors) {
          const mirrors = new Map(changes.set.filter(write => write.path[0] !== 'computed').map(write => [write.path.join('.'), write.value]));
          delta.mirrors.set = delta.mirrors.set.map(write => mirrors.has(write.path.join('.')) ? { ...write, value: mirrors.get(write.path.join('.')) } : write);
          delta.mirrors.before = delta.mirrors.before?.filter(write => !mirrors.has(write.path.join('.')));
        }
      }
      publishSavedDice({ ...body, actor_id: actorId }, result, applied);
      window.dispatchEvent(new CustomEvent('ft:charge-truth-committed', { detail: { body, result } }));
      if (marker.fortunateOffers.length) offerFortunate(encounterId, actorId, marker.fortunateOffers);
      return;
    }
    // The silent Psi/Ether adjust is the out-of-combat pin. The encounter
    // row won this CAS: restore the optimistic count and wait for its combat frame.
    if (status === 409 && result?.error === 'In combat' && body.kind === 'adjust' && !body.log
      && (itemId === PSI_MECHANIC_ID || itemId === ETHER_MECHANIC_ID)) {
      if (settle()) install(res.kind, encounterId, actorId, itemId, pool.value ?? res.before);
      input.onCombatStarted?.(); // local watcher notification; never part of the wire command
      window.dispatchEvent(new CustomEvent('ft:charge-truth-reconciled', { detail: { body, combatStarted: true } }));
      return;
    }
    if (status === 409 && current !== null) {
      const idle = settle();
      // A retried operation whose first answer was lost reads its own write back as "stale": that IS success.
      if (!acceptServer(res.kind, encounterId, actorId, itemId, current, rev, oid) && idle && pool.value !== null) install(res.kind, encounterId, actorId, itemId, pool.value);
      if (current !== marker.after) warn('The charges changed elsewhere — the current count was restored. Adjust again if needed.');
      window.dispatchEvent(new CustomEvent('ft:charge-truth-reconciled', { detail: { body, current } }));
      return;
    }
    if (status >= 400 && status < 500) {
      // Refused: put the pool back to the newest count the server confirmed (else what this click read), once nothing else is in flight.
      if (settle()) install(res.kind, encounterId, actorId, itemId, pool.value ?? res.before);
      warn(status === 403 ? 'You do not have permission to adjust the charges on this.' : 'The charge change was refused: ' + String(result?.error ?? status));
      window.dispatchEvent(new CustomEvent('ft:charge-truth-refused', { detail: { body, status, result } }));
      if (status === 409) await window.ftResyncEncounter?.(encounterId).catch(() => {});
      return;
    }
    // Never answered: the change may have committed; the committed / correction frame settles it. Show the newest accepted count.
    settle();
    drain();
    warn('The charge change could not be confirmed (' + String(result?.error ?? status) + '). Check the count before changing it again.');
  };
  queueActorSave({ encounterId, actorIds: [actorId, ...confirmed.map(row => row.actorId)], command: body, confirmed,
    send: async () => {
      const answer = await post(body);
      if (answer.status === 0 || answer.status >= 500) {
        await applyAnswer(answer);
        throw new Error(String(answer.result?.error ?? 'charge-save-timeout'));
      }
      if (answer.status >= 400) return { ok: false, status: answer.status, error: String(answer.result?.error ?? 'charge refused'), result: answer.result };
      await applyAnswer(answer);
      return { ok: true };
    },
    refused: async info => {
      if (info.chained) { pool.pending = pool.pending.filter(op => op !== oid); return; }
      await applyAnswer({ status: info.status, result: asRow(info.body?.result) ?? { error: info.error } });
      if (info.status !== 409) await window.ftResyncEncounter?.(encounterId).catch(() => {});
    },
  });
  return 'handled';
}

let lastChargeReason = '';
const decisionHasWrites = ({ verdict: v }: ChargeMarkerDecision): boolean => Object.keys(v.ec).length > 0
  || [v.statusesAdded, v.statusesRemoved, v.statusStacks, v.charges, v.playerCharactersChanged].some(rows => rows.length > 0);
function chargeWords(message: string): 'refused' {
  warn(message);
  window.dispatchEvent(new CustomEvent('ft:charge-truth-refused-words', { detail: { message } }));
  return 'refused';
}
function chargeReasonWords(reason: string): string {
  switch (reason) {
    case 'delta-not-integer': case 'value-not-integer': return 'The charges were not changed: enter a whole number.';
    case 'ability-entry-not-held': return 'The charges were not changed: this creature does not hold that ability.';
    case 'inventory-row-not-held': return 'The charges were not changed: that item is not carried by this creature.';
    case 'item-charges-not-held': return 'That item has no charges.';
    case 'current-charges-not-held': case 'maximum-charges-not-held': return 'The charges were not changed: they could not be read on this page. Reload the page and try again.';
    case 'projectile-pool-undecidable': return 'The charges were not changed: this volley can only be spent from the character sheet.';
    default: return 'The charges were not changed: ' + (reason || 'charge-context-not-held') + '. Reload the page and try again.';
  }
}
/** The ONE caller seam (inventory-actions adjustCharge / changeCharges / changeInventoryCharges): never a legacy post. */
export function commitCharge(input: ChargeCommitInput): Promise<ChargeCommitOutcome> {
  // Admission in CLICK order, per actor, from before the first await (Codex N6 review P2).
  return runRowOnly(input.casterId, () => commitChargeNow(input));
}
async function commitChargeNow(input: ChargeCommitInput): Promise<ChargeCommitOutcome> {
  const where = await ensureActorRowHeld(input.casterId, input.encounterId, { allowRowOnly: true, what: 'The charges' });
  if (where.kind === 'refused') return chargeWords(where.message);
  // N6c (owner 2026-10-01): an actor in NO encounter is not controlled — its charges are not set or spent there. Refused, never posted
  // (the row-only mode of charge_truth_commit_inc.php is deleted).
  if (where.kind === 'row') return chargeWords('The charges were not changed: a creature outside an encounter cannot use or reset its charges. Bring it into an encounter first.');
  const at = { ...input, casterId: where.actorId, encounterId: where.encounterId };
  // Load genuinely absent context/definitions once, through this bundle's normal readers and caches.
  const caster = rowsFor(where.encounterId, where.actorId)[0]!.row;
  const count = (): ChargeResolution => input.kind === 'adjust' ? resolveAdjustCharge(caster, upper(input.itemId), input.delta, readers, where.actorId)
    : input.kind === 'set' ? resolveSetCharge(caster, upper(input.itemId), input.value, readers) : resolveInventoryCharge(caster, upper(input.itemId), input.value, readers);
  let resolved = count();
  if (!resolved.supported && resolved.reason === 'maximum-charges-not-held') { await fetchAndCacheObject(upper(input.itemId)).catch(() => null); resolved = count(); }
  const consent = createTriggerConsent(where.encounterId);
  if (resolved.supported && chargeMarkerDue(resolved)) {
    const encounter = asRow(actorEncounterFor(where.encounterId));
    const campaignId = upper(encounterField(encounter, 'encounter_master_parent_id'));
    if (encounter && encounterField(encounter, 'encounter_master_parent_type') === 'campaign' && !abilityTruthCampaign(encounter)) {
      const campaign = asRow(await fetchAndCacheObject(campaignId).catch(() => null));
      if (campaign && asRow(campaign.campaigns)) {
        window.FateTide!.campaignCache ??= new Map();
        window.FateTide!.campaignCache.set(campaignId, campaign as unknown as CampaignArr);
      }
    }
    const context = chargeContext(where.encounterId, caster);
    if (!context) return chargeWords(chargeReasonWords(lastChargeReason));
    const read = abilityTruthReaders(caster, context.encounter, new Set(context.members.keys()));
    if (read.campaignClock()) {
      if (!fullDefinition(read.definition(HOLDING_MASTERY), 'any')) await fetchObjectsBatch([HOLDING_MASTERY]);
      await hydrateTurnTriggerDefinitions(caster, context.encounter, [], [...context.members.values()]);
    }
  }
  lastChargeReason = '';
  let outcome = tryCommitCharge(at, consent);
  if (outcome === null && consent.pending().length) {
    await askTriggerConsent(where.encounterId, where.actorId, consent);
    lastChargeReason = '';
    outcome = tryCommitCharge(at, consent);
  }
  return outcome ?? chargeWords(chargeReasonWords(lastChargeReason));
}

interface ChargeTruthFrame { schema: string; operation_id: string; encounter_id: string; actor_id: string; kind: ChargeKind; item_id: string; current: number; rev?: number; committed?: boolean; correction?: boolean }
export function chargeTruthFrameIsNewer(frame: ChargeTruthFrame): boolean {
  return revOf(frame.rev) > (pools.get(upper(frame.actor_id) + ':' + upper(frame.item_id))?.rev ?? -1);
}
/** Committed / correction frames from any page, ordered by the pool revision. */
export function handleChargeTruth(frame: ChargeTruthFrame): void {
  if (frame?.schema !== CHARGE_SCHEMA) return;
  const current = countOf(frame.current);
  if (current === null) return;
  const installed = acceptServer(frame.kind, upper(frame.encounter_id), upper(frame.actor_id), upper(frame.item_id), current, revOf(frame.rev), upper(frame.operation_id));
  window.dispatchEvent(new CustomEvent('ft:charge-truth-frame', { detail: { ...frame, installed } }));
}

/** Re-render a caster row the page mutated in place (the local caster_update path) — for other bundles' reconcilers. */
function renderCasterRowLocal(row: unknown, changedPaths?: readonly string[]): void {
  if (row && typeof row === 'object') handleCasterUpdate({ type: 'caster_update', caster_arr: row as CasterArr, local: true }, changedPaths);
}

Object.assign(window, { ftTryCommitCharge: tryCommitCharge, ftCommitCharge: commitCharge, ftRenderCasterRowLocal: renderCasterRowLocal });
